import { Input, Component } from '@angular/core';
import * as constants from '../constants-types/constants';
import { Table } from '../core-services/Tables/Table';
import { Util } from '../core-utilities/Util';
import { TranslationService } from '../translations/Translation.service';
import { PagerUtilities } from './Pager.utility';

@Component({
    selector: 'pi-pager',
    template: `
        @if (util.hasValue(pagingData?.maxResults)) {
            <div class="pi-pagination">
                <div
                    data-testing="pagingAndTotalCountInformation"
                    class="pi-pagination_resultSizeInformation"
                >
                    {{ resultSizeInformation() }}
                </div>
                <div>
                    <span
                        class="pi-pagination_pageNumber"
                        [ngClass]="{
                            'pi-pagination_pageNumber--clickableNumber':
                                1 < pagingData.currentPageNumber
                        }"
                        (click)="goToPage(pagingData.currentPageNumber - 1)"
                    >
                        {{ TR.key('text.common-terms.prev') }}
                    </span>
                    @for (
                        pageNumber of pagerUtil.pagingNumbersDisplay(
                            pagingData.currentPageNumber,
                            getMaxPages
                        );
                        track pageNumber
                    ) {
                        <span
                            class="pi-pagination_pageNumber"
                            [ngClass]="{
                                'pi-pagination_pageNumber--clickableNumber':
                                    isClickablePage(pageNumber),
                                'pi-pagination_pageNumber--currentPage':
                                    isCurrentPage(pageNumber)
                            }"
                            (click)="goToPage(pageNumber)"
                        >
                            @if (pageNumber === ellipsisValue) {
                                <span>...</span>
                            } @else {
                                {{ pageNumber }}
                            }
                        </span>
                    }
                    <span
                        class="pi-pagination_pageNumber"
                        [ngClass]="{
                            'pi-pagination_pageNumber--clickableNumber':
                                this.pagingData.maxResults /
                                    this.pagingData.resultsPerPage >
                                pagingData.currentPageNumber
                        }"
                        (click)="goToPage(pagingData.currentPageNumber + 1)"
                    >
                        {{ TR.key('text.common-terms.next') }}
                    </span>
                </div>
                <pi-pager-results-size-picker
                    [searchState]="tableData.searchState"
                    (onResultsSizeChange)="updateResultsPerPage()"
                >
                </pi-pager-results-size-picker>
            </div>
        }
    `,
    standalone: false
})
export class TablePagerComponent {
    /* Changes were made to directly insert the Table Data structure. This eliminated code, but
        does increase the coupling between the Table Data structure and the Pager. We should
        consider making the Table Data structure meet an interface that the Pager takes. This
        would undo this coupling. Do that when we need to decouple
    */

    @Input()
    tableData: Table<any>;

    resultsPerPageOptions = [
        constants.tableDefaultResultsPerPage,
        constants.serverApiMaxResults
    ];

    // To display the "..."
    ellipsisValue = -1;

    constructor(
        public TR: TranslationService,
        public util: Util,
        public pagerUtil: PagerUtilities
    ) {}

    get pagingData() {
        return this.tableData.searchState.pageState;
    }

    get getMaxPages() {
        return Math.ceil(
            this.pagingData.maxResults / this.pagingData.resultsPerPage
        );
    }

    isClickablePage(pageNumber: number): boolean {
        return (
            pageNumber !== this.pagingData.currentPageNumber &&
            pageNumber !== this.ellipsisValue
        );
    }

    isCurrentPage(pageNumber: number): boolean {
        return pageNumber === this.pagingData.currentPageNumber;
    }

    resultSizeInformation() {
        if (this.pagingData.maxResults === 0) {
            return this.TR.key('tablePager.noResultsFound');
        }

        let currentResultRange = `${
            (this.pagingData.currentPageNumber - 1) *
                this.pagingData.resultsPerPage +
            1
        }-`;
        if (
            this.pagingData.currentPageNumber * this.pagingData.resultsPerPage >
            this.pagingData.maxResults
        ) {
            currentResultRange += this.pagingData.maxResults;
        } else {
            currentResultRange += `${
                this.pagingData.currentPageNumber *
                this.pagingData.resultsPerPage
            }`;
        }

        return this.util.strings.formatString(
            this.TR.key('tablePager.resultSize'),
            currentResultRange,
            this.pagingData.maxResults
        );
    }

    updateResultsPerPage() {
        this.tableData.search();
    }

    goToPage(pageNumber: number) {
        // Don't bother
        if (
            pageNumber === this.pagingData.currentPageNumber ||
            // Don't go past bounds
            pageNumber < 1 ||
            pageNumber > this.getMaxPages
        ) {
            return;
        }
        this.tableData.searchState =
            this.tableData.searchState.setPage(pageNumber);
        this.tableData.search();
    }
}
